import React, { useMemo, useState } from 'react'
import type { GridController } from '../../compositor/GridController'
import type { FormulaEditSession } from '../../spreadsheet/formulaAuthoring'
import type { SpreadsheetSelectionRange } from '../../spreadsheet/addressing'
import { formatSelectionSummaryNumber, summarizeSelectionValues } from '../../spreadsheet/selectionSummary'
import { getSpreadsheetCommand, type SpreadsheetCommandId } from '../../spreadsheet/commandRegistry'
import { ContextMenu } from '../ContextMenu'
import { getFuseGridButtonIcon } from '../icons/FuseGridButtonIcons'
import './ExcelWorkbookChrome.css'

type ExcelStatusBarProps = {
  controller?: GridController | null
  selectedRange?: SpreadsheetSelectionRange | null
  formulaSession?: FormulaEditSession | null
  protectionStatus?: {
    label: string
    detail?: string
    locked?: boolean
  } | null
  mockSummary?: {
    mode?: string
    average?: string
    count?: string
    numericalCount?: string
    sum?: string
  }
  onViewStateChange?: () => void
}

function getMode(formulaSession?: FormulaEditSession | null, fallback?: string): string {
  if (fallback) return fallback
  if (!formulaSession || formulaSession.status !== 'active') return 'Ready'
  if (formulaSession.mode === 'point' || formulaSession.mode === 'range-resize') return 'Point'
  if (formulaSession.mode === 'value') return 'Enter'
  return 'Edit'
}

type StatusSummaryKey = 'average' | 'count' | 'numericalCount' | 'sum'

const STATUS_SUMMARY_COMMANDS: Array<{
  key: StatusSummaryKey
  commandId: SpreadsheetCommandId
}> = [
  { key: 'average', commandId: 'status-average' },
  { key: 'count', commandId: 'status-count' },
  { key: 'numericalCount', commandId: 'status-numerical-count' },
  { key: 'sum', commandId: 'status-sum' },
]

const DEFAULT_VISIBLE_SUMMARIES: Record<StatusSummaryKey, boolean> = {
  average: true,
  count: true,
  numericalCount: true,
  sum: true,
}

const NormalViewIcon = getFuseGridButtonIcon('view-normal')!
const PageLayoutIcon = getFuseGridButtonIcon('view-page-layout')!
const PageBreakIcon = getFuseGridButtonIcon('view-page-break')!
const ZoomOutIcon = getFuseGridButtonIcon('zoom-out')!
const ZoomInIcon = getFuseGridButtonIcon('zoom-in')!

export function ExcelStatusBar({
  controller,
  selectedRange,
  formulaSession,
  protectionStatus,
  mockSummary,
  onViewStateChange,
}: ExcelStatusBarProps) {
  const [, setViewRevision] = useState(0)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)
  const [visibleSummaries, setVisibleSummaries] = useState<Record<StatusSummaryKey, boolean>>(DEFAULT_VISIBLE_SUMMARIES)
  const summary = useMemo(() => {
    if (mockSummary) return null
    const selection = selectedRange ?? controller?.selection ?? null
    if (!controller || !selection) return summarizeSelectionValues(null, () => undefined)
    return summarizeSelectionValues(selection, (col, row) => controller.getCellValue(col, row))
  }, [controller, mockSummary, selectedRange])

  const mode = getMode(formulaSession, mockSummary?.mode)
  const average = mockSummary?.average ?? (summary?.average != null ? formatSelectionSummaryNumber(summary.average) : '')
  const count = mockSummary?.count ?? String(summary?.count ?? 0)
  const numericalCount = mockSummary?.numericalCount ?? String(summary?.numericalCount ?? 0)
  const sum = mockSummary?.sum ?? formatSelectionSummaryNumber(summary?.sum ?? 0)
  const zoomPercent = Math.round((controller?.getZoom() ?? 1) * 100)

  const statusContextMenuItems = useMemo(() => {
    return [
      ...STATUS_SUMMARY_COMMANDS.map(({ key, commandId }) => {
        const command = getSpreadsheetCommand(commandId)
        return {
          label: command.label,
          checked: visibleSummaries[key],
          title: command.description,
          action: () => setVisibleSummaries((current) => ({
            ...current,
            [key]: !current[key],
          })),
        }
      }),
      { type: 'separator' as const },
      {
        label: 'Reset Status Bar',
        title: 'Show all selection summaries in the status bar.',
        action: () => setVisibleSummaries(DEFAULT_VISIBLE_SUMMARIES),
      },
    ]
  }, [visibleSummaries])

  const setZoomPercent = (percent: number) => {
    if (!controller) return
    controller.setZoom(percent / 100)
    setViewRevision((revision) => revision + 1)
    onViewStateChange?.()
  }

  return (
    <div
      className="fg-excel-chrome fg-excel-statusbar"
      data-testid="fg-excel-statusbar"
      onContextMenu={(event) => {
        event.preventDefault()
        setContextMenu({ x: event.clientX, y: event.clientY })
      }}
    >
      <span className="fg-excel-status-mode" title={`Mode: ${mode}`}>{mode}</span>
      {protectionStatus ? (
        <span
          className={`fg-excel-status-pill fg-excel-protection-status${protectionStatus.locked === false ? ' is-unlocked' : ''}`}
          title={protectionStatus.detail || protectionStatus.label}
        >
          Protection: <strong>{protectionStatus.label}</strong>
        </span>
      ) : null}
      {visibleSummaries.average && average && <span className="fg-excel-status-pill" title={`Average: ${average}`}>Average: <strong>{average}</strong></span>}
      {visibleSummaries.count && <span className="fg-excel-status-pill" title={`Count: ${count}`}>Count: <strong>{count}</strong></span>}
      {visibleSummaries.numericalCount && <span className="fg-excel-status-pill" title={`Numerical Count: ${numericalCount}`}>Numerical Count: <strong>{numericalCount}</strong></span>}
      {visibleSummaries.sum && <span className="fg-excel-status-pill" title={`Sum: ${sum}`}>Sum: <strong>{sum}</strong></span>}
      {summary?.truncated && <span className="fg-excel-status-pill" title="Summary sampled the first cells only">sampled</span>}
      <span className="fg-excel-status-spacer" />
      <span className="fg-excel-view-shortcuts" aria-label="View shortcuts">
        <button type="button" className="fg-excel-icon-button" title="Normal view" aria-label="Normal view" disabled>
          <NormalViewIcon size={15} />
        </button>
        <button type="button" className="fg-excel-icon-button" title="Page layout view" aria-label="Page layout view" disabled>
          <PageLayoutIcon size={15} />
        </button>
        <button type="button" className="fg-excel-icon-button" title="Page break preview" aria-label="Page break preview" disabled>
          <PageBreakIcon size={15} />
        </button>
      </span>
      <span className="fg-excel-zoom" aria-label="Zoom">
        <button
          type="button"
          className="fg-excel-icon-button fg-excel-zoom-button"
          title="Zoom out"
          aria-label="Zoom out"
          disabled={!controller}
          onClick={() => setZoomPercent(Math.max(50, zoomPercent - 10))}
        >
          <ZoomOutIcon size={14} />
        </button>
        <input
          className="fg-excel-zoom-range"
          type="range"
          min={50}
          max={200}
          step={10}
          value={zoomPercent}
          aria-label="Zoom"
          title="Zoom"
          disabled={!controller}
          onChange={(event) => setZoomPercent(Number(event.target.value))}
        />
        <button
          type="button"
          className="fg-excel-icon-button fg-excel-zoom-button"
          title="Zoom in"
          aria-label="Zoom in"
          disabled={!controller}
          onClick={() => setZoomPercent(Math.min(200, zoomPercent + 10))}
        >
          <ZoomInIcon size={14} />
        </button>
        <strong title={`Zoom: ${zoomPercent}%`}>{zoomPercent}%</strong>
      </span>
      {contextMenu ? (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={statusContextMenuItems}
          onClose={() => setContextMenu(null)}
        />
      ) : null}
    </div>
  )
}
